// src/containers/VehicleList/VehicleDisplay/VehicleTab/MpiFormBuilder/hooks/useFormInstructions.js
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';

const useFormInstructions = (tab) => {
  const all_tabs = useSelector(state => state.settings_site?.all_tabs || []);
  const custom_forms = useSelector(state => state.settings_refresh?.custom_forms || []);

  const currentFormName = useMemo(() => {
    const tabObj = all_tabs.find(t => t.name === tab);
    return tabObj?.form || tab || '';
  }, [all_tabs, tab]);

  const formInstructions = useMemo(() => {
    const defaultFormInstructions = {
      sections: [],
      headers: {},
      orderedPresetIds: {},
      sectionTotaling: {},
      formTotaling: {
        approved: { enabled: true, equation: 'estimate_total', conditions: [{ header: 'approved', operator: 'equals', value: 'true' }], filters: [] },
        completed: { enabled: true, equation: 'estimate_total', conditions: [{ header: 'completed', operator: 'equals', value: 'true' }], filters: [] },
        pending: { enabled: true, equation: 'estimate_total', conditions: [
          { header: 'recommend', operator: 'equals', value: 'true' },
          { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
          { header: 'approved', operator: 'equals', value: 'false' },
          { header: 'completed', operator: 'equals', value: 'false' },
        ], filters: [] },
        grand: { enabled: true, equation: 'estimate_total', conditions: [
          { header: 'recommend', operator: 'equals', value: 'true' },
          { header: 'required', operator: 'equals', value: 'true', operatorType: 'or' },
          { header: 'approved', operator: 'equals', value: 'true', operatorType: 'or' },
        ], filters: [] },
        parts: { enabled: true, equation: 'parts_total', conditions: [], filters: [] },
        labor: { enabled: true, equation: 'labor_total', conditions: [], filters: [] },
      },
      currentFormName,
      formBackground: { color: '#ffffff', image: '' },
      form_settings: {},
      customFormId: null,   // NEW
      sendSftp: true,       // NEW - default to true for backward compatibility
    };

    const form = _.find(custom_forms, f => f.name === currentFormName);
    if (!form?.custom_form_properties) {
      return defaultFormInstructions;
    }

    const props = form.custom_form_properties;
    const sortedProps = _.sortBy(props, 'order');

    // NEW: Extract customFormId and send_sftp flag
    const customFormId = form.id || null;
    const sendSftpProp = props.find(p => p.key === 'send_sftp');
    const sendSftp = sendSftpProp 
      ? (sendSftpProp.value === 'true' || sendSftpProp.value === true || sendSftpProp.value === '1')
      : true;

    // Extract sections with their min order and order prop if exists
    const sectionsInfo = {};
    sortedProps.forEach(p => {
      const match = p.key.match(/section_(.+)_id/);
      if (match) {
        const sectionId = match[1];
        if (sectionId && sectionId !== 'undefined') {
          sectionsInfo[sectionId] = { minOrder: p.order, order: p.order, props: [] };
        }
      }
    });

    // Update explicit order if `section_xxx_order` exists (future-proof)
    sortedProps.forEach(p => {
      const matchOrder = p.key.match(/section_(.+)_order/);
      if (matchOrder) {
        const sectionId = matchOrder[1];
        if (sectionsInfo[sectionId]) {
          sectionsInfo[sectionId].order = parseInt(p.value, 10) || sectionsInfo[sectionId].order;
        }
      }
    });

    // Group properties by section - expanded to include header sub-props
    const suffixRegex = '(label|layout|totaling|background|headers|items|header_color|font_color|header_font_color|header_\\d+_.+)';
    sortedProps.forEach(p => {
      const match = p.key.match(new RegExp(`section_(.+)_${suffixRegex}$`));
      if (match) {
        const sectionId = match[1];
        if (sectionsInfo[sectionId]) {
          sectionsInfo[sectionId].props.push(p);
        }
      }
    });

    // Sort sections by explicit order or minOrder fallback
    const sortedSectionsIds = _.sortBy(Object.keys(sectionsInfo), id => sectionsInfo[id].order);

    const sections = [];
    const headers = {};
    const orderedPresetIds = {};
    const sectionTotaling = {};
    let formTotaling = defaultFormInstructions.formTotaling;
    let formBackground = defaultFormInstructions.formBackground;
    let form_settings = {};

    // Load form_settings first to get defaults
    const settingsProp = sortedProps.find(p => p.key === 'form_settings');
    if (settingsProp?.value) {
      try {
        form_settings = JSON.parse(settingsProp.value);
        formBackground = {
          color: form_settings.backgroundColor || '#ffffff',
          image: form_settings.backgroundImage || '',
        };
      } catch (e) {}
    }

    const defaultHeaderColor = form_settings.sectionHeaderColor || '#f5f5f5';
    const defaultFontColor = form_settings.fontColor || '#000000';
    const defaultHeaderFontColor = form_settings.sectionHeaderFontColor || '#000000';

    sortedSectionsIds.forEach(sectionId => {
      const sectionProps = sectionsInfo[sectionId].props;
      let label = sectionId.replace(/_/g, ' ').toUpperCase();
      let layout = { width: '100%', align: 'left' };
      let totaling = { subtotal: false, equation: 'estimate_total' };
      let background = { color: '', image: '' };
      let headerColor = defaultHeaderColor;
      let fontColor = defaultFontColor;
      let headerFontColor = defaultHeaderFontColor;
      let sectionHeaders = [];
      let presetIds = [];

      sectionProps.forEach(p => {
        if (p.key === `section_${sectionId}_label`) {
          label = p.value || label;
        } else if (p.key === `section_${sectionId}_layout`) {
          try {
            layout = JSON.parse(p.value) || layout;
          } catch (e) {}
        } else if (p.key === `section_${sectionId}_totaling`) {
          try {
            totaling = JSON.parse(p.value) || totaling;
          } catch (e) {}
        } else if (p.key === `section_${sectionId}_background`) {
          try {
            background = JSON.parse(p.value) || background;
          } catch (e) {}
        } else if (p.key === `section_${sectionId}_header_color`) {
          headerColor = p.value || headerColor;
        } else if (p.key === `section_${sectionId}_header_font_color`) {
          headerFontColor = p.value || headerFontColor;
        } else if (p.key === `section_${sectionId}_font_color`) {
          fontColor = p.value || fontColor;
        } else if (p.key === `section_${sectionId}_headers`) {
          const pairs = p.value.split(',').filter(Boolean);
          sectionHeaders = pairs.map((pair, idx) => {
            const parts = pair.split(':').map(pt => pt.trim());
            const field = parts[0];
            const cleanLabel = parts[1] || field.replace(/_/g, ' ');
            const icon = parts[2] || null;
            const color = parts[3] || null;

            const allowedProfilesProp = sectionProps.find(pr => pr.key === `section_${sectionId}_header_${idx}_allowed_profiles`);
            const inputTypeProp = sectionProps.find(pr => pr.key === `section_${sectionId}_header_${idx}_inputtype`);
            const trueColorProp = sectionProps.find(pr => pr.key === `section_${sectionId}_header_${idx}_true_color`);
            const iconProp = sectionProps.find(pr => pr.key === `section_${sectionId}_header_${idx}_icon`);

            return {
              field,
              label: cleanLabel,
              allowed_profiles: allowedProfilesProp?.value || '',
              inputtype: inputTypeProp?.value || '',
              icon: iconProp?.value || icon,
              true_color: trueColorProp?.value || color,
            };
          });
        } else if (p.key === `section_${sectionId}_items`) {
          presetIds.push({
            presetId: parseInt(p.value),
            order: p.order,
          });
        }
      });

      presetIds.sort((a, b) => a.order - b.order);
      orderedPresetIds[sectionId] = presetIds.map(o => o.presetId);

      sections.push({
        id: sectionId,
        label,
        layout,
        background,
        headerColor,
        fontColor,
        headerFontColor,
        order: sectionsInfo[sectionId].order,
      });
      headers[sectionId] = sectionHeaders;
      sectionTotaling[sectionId] = totaling;
    });

    // Form totaling
    const totalingProp = sortedProps.find(p => p.key === 'form_totaling');
    if (totalingProp?.value) {
      try {
        const parsed = JSON.parse(totalingProp.value);
        formTotaling = {
          approved: parsed.approved || defaultFormInstructions.formTotaling.approved,
          completed: parsed.completed || defaultFormInstructions.formTotaling.completed,
          pending: parsed.pending || defaultFormInstructions.formTotaling.pending,
          grand: parsed.grand || defaultFormInstructions.formTotaling.grand,
          parts: parsed.parts || defaultFormInstructions.formTotaling.parts,
          labor: parsed.labor || defaultFormInstructions.formTotaling.labor,
        };
      } catch (e) {}
    }

    return {
      sections,
      headers,
      orderedPresetIds,
      sectionTotaling,
      formTotaling,
      currentFormName,
      formBackground,
      form_settings,
      customFormId,
      sendSftp,
    };
  }, [custom_forms, currentFormName, all_tabs, tab]);

  return formInstructions;
};

export default useFormInstructions;